/**
 * Reusable loader for content areas (not full screen)
 * Copy and action presets live in `loader-messages.ts` — change there to update app-wide
 */

import * as React from 'react'
import { cn } from '@/lib/utils'
import { H3, Body } from '@/components/ui/typography'
import { Spinner } from './Spinner'
import { contentLoaderActionMessages } from './loader-messages'

export type LoaderAction = keyof typeof contentLoaderActionMessages

export interface ContentLoaderProps {
  /**
   * Action type — determines which message set from `contentLoaderActionMessages` is used
   */
  action?: LoaderAction
  /**
   * Custom title (overrides rotating action messages when set)
   */
  title?: string
  /**
   * Custom description
   */
  description?: string
  /**
   * Replaces `{entity}` in rotating messages
   */
  entityName?: string
  showProgress?: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
  minHeight?: string
}

const spinnerSizeMap = {
  sm: 'sm' as const,
  md: 'md' as const,
  lg: 'xl' as const,
}

export function ContentLoader({
  action = 'load',
  title,
  description,
  entityName = 'page',
  showProgress = true,
  size = 'lg',
  className,
  minHeight = 'min-h-[400px]',
}: ContentLoaderProps) {
  const [currentMessageIndex, setCurrentMessageIndex] = React.useState(0)
  const [progressDots, setProgressDots] = React.useState('')

  const messageList = contentLoaderActionMessages[action]

  React.useEffect(() => {
    setCurrentMessageIndex(0)
  }, [action])

  React.useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % messageList.length)
    }, 2000)

    return () => clearInterval(messageInterval)
  }, [messageList.length, action])

  React.useEffect(() => {
    if (!showProgress) return
    const dotsInterval = setInterval(() => {
      setProgressDots((prev) => {
        if (prev === '') return '.'
        if (prev === '.') return '..'
        if (prev === '..') return '...'
        return ''
      })
    }, 500)

    return () => clearInterval(dotsInterval)
  }, [showProgress])

  const getMessage = () => {
    if (title) return title
    return messageList[currentMessageIndex % messageList.length].replace('{entity}', entityName)
  }

  const sizeConfig = {
    sm: { text: 'text-sm', gap: 'gap-2' },
    md: { text: 'text-base', gap: 'gap-3' },
    lg: { text: 'text-lg', gap: 'gap-4' },
  }

  const currentSize = sizeConfig[size]

  return (
    <div
      className={cn('flex w-full items-center justify-center', minHeight, className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className={cn('flex flex-col items-center justify-center', currentSize.gap)}>
        <Spinner size={spinnerSizeMap[size]} variant="primary" />

        <div className="flex max-w-md flex-col items-center gap-2 px-4 text-center">
          <H3 className={cn(currentSize.text, 'font-semibold')}>
            {getMessage()}
            {showProgress && <span className="inline-block w-6 text-left">{progressDots}</span>}
          </H3>
          {description && (
            <Body className="text-sm font-medium text-primary/90">{description}</Body>
          )}
        </div>

        {showProgress && (
          <div
            className="content-loader-progress mx-auto"
            role="progressbar"
            aria-valuetext="Loading"
          >
            <div className="content-loader-progress__indeterminate" aria-hidden />
          </div>
        )}
      </div>
    </div>
  )
}

ContentLoader.displayName = 'ContentLoader'
